<script setup lang="ts">
import { UseMouseInElement } from "@vueuse/components";

const props = defineProps({
  modelValue: {
    type: Object as PropType<any>,
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const showHeader = computed(() => {
  if (props.modelValue == undefined) {
    return false;
  }

  return (
    props.modelValue.includePageNumbers == true ||
    (props.modelValue.extraText != undefined &&
      props.modelValue.extraText != "")
  );
});

const headerController = useNewDialogController();

const editableHeader = computed(() => {
  if (props.modelValue != undefined) {
    return props.modelValue;
  }

  return {
    alignment: "right" as "left" | "center" | "right",
    extraText: undefined,
    includePageNumbers: false,
  };
});

const updateHeader = (header?: {
  alignment: "left" | "center" | "right";
  extraText?: string;
  includePageNumbers: boolean;
}) => {
  emit("update:modelValue", header);
  headerController.value.close();
};

const isAddFormOpen = ref(false);
const isEditFormOpen = ref(false);
</script>

<template>
  <div class="flex flex-col">
    <UseMouseInElement v-slot="{ isOutside }" v-auto-animate>
      <div class="header-container h-[80px] w-full relative">
        <div
          v-if="modelValue != undefined && showHeader"
          class="w-full h-full flex flex-row items-center"
          :class="{
            'justify-start': modelValue.alignment == 'left',
            'justify-center': modelValue.alignment == 'center',
            'justify-end': modelValue.alignment == 'right',
          }"
        >
          <div
            v-if="readOnly"
            class="min-w-[10px] px-1 flex flex-row items-center gap-1 rounded text-[12pt] text-medium"
          >
            <span
              v-if="modelValue.extraText"
              :class="{
                'order-1':
                  modelValue.alignment == 'right' ||
                  modelValue.alignment == 'center',
                'order-2': modelValue.alignment == 'left',
              }"
            >
              {{ modelValue.extraText }}
            </span>
            <span
              v-if="modelValue.includePageNumbers"
              :class="{
                'order-1':
                  modelValue.alignment == 'right' ||
                  modelValue.alignment == 'center',
                'order-2': modelValue.alignment == 'left',
              }"
            >
              1
            </span>
          </div>
          <BasePopover
            v-else
            :controller="headerController"
            :align="modelValue.alignment == 'left' ? 'start' : 'end'"
            @update:open="isEditFormOpen = $event"
          >
            <div
              class="min-w-[10px] px-1 hover:bg-light-default flex flex-row items-center gap-1 cursor-pointer rounded text-[12pt] text-medium"
            >
              <span
                v-if="modelValue.extraText"
                :class="{
                  'order-1':
                    modelValue.alignment == 'right' ||
                    modelValue.alignment == 'center',
                  'order-2': modelValue.alignment == 'left',
                }"
              >
                {{ modelValue.extraText }}
              </span>
              <span
                v-if="modelValue.includePageNumbers"
                :class="{
                  'order-1':
                    modelValue.alignment == 'right' ||
                    modelValue.alignment == 'center',
                  'order-2': modelValue.alignment == 'left',
                }"
                >1</span
              >
            </div>
            <template #content>
              <div class="bg-surface p-2 rounded w-[200px]">
                <DocumentEditorHeaderForm
                  :model-value="modelValue"
                  :show-remove-button="true"
                  @update:model-value="updateHeader"
                />
              </div>
            </template>
          </BasePopover>
        </div>
        <div
          v-if="
            readOnly != true &&
            (isAddFormOpen == true ||
              ((modelValue == undefined || !showHeader) && isOutside != true))
          "
          class="w-full h-full flex flex-row items-center justify-end"
        >
          <BasePopover
            :controller="headerController"
            align="end"
            @update:open="isAddFormOpen = $event"
          >
            <span
              class="text-body-2 text-medium hover:underline cursor-pointer"
            >
              Add Header
            </span>

            <template #content>
              <div class="bg-surface p-2 rounded w-[200px]">
                <BaseWordProcessorHeaderForm
                  :model-value="editableHeader"
                  @update:model-value="updateHeader"
                />
              </div>
            </template>
          </BasePopover>
        </div>
      </div>
    </UseMouseInElement>
  </div>
</template>

<style scoped>
.header-container {
  padding: 0 48px;
}

.header-container {
  font-size: 12pt;
  font-family: "Times New Roman", serif;
}
</style>
